/**
 * CarveConfig defaults, normalization, diffing, hashing, and human-readable summary.
 *
 * The DEFAULT_BASE_CONFIG is the seed for the single active "base" logic version.
 * Managers fork local copies and (via the LLM) produce partial diffs against it; we
 * always normalize the merged result back into a full, valid CarveConfig before a run.
 */

import type { CarveConfig, CarveWeights, ExpansionComponent, ExpansionModel } from './engine/types';
import { rampDateFor } from './engine/ramp';

/**
 * Built-in expansion components — the v3 model: six percentile/bucket sub-scores averaged, then
 * four ±10 penalty/bonus flags. Shipped disabled so the default reproduces the precomputed v3
 * composite; enabling it lets managers re-weight / band these and add their own.
 */
/** Bumped whenever the built-in stock component set changes, to one-time backfill legacy carves. */
export const EXPANSION_MODEL_VERSION = 3;

/** Canonical built-in (stock) component keys, in display order. */
export const STOCK_EXPANSION_KEYS = [
  'seatHeadroom',
  'funding',
  'sentiment',
  'pacing',
  'execEngagement',
  'empGrowthYoy',
  'amendmentBonus',
  'fundingEvent',
  'peBacked',
  'recentCw',
] as const;

export const DEFAULT_EXPANSION_MODEL: ExpansionModel = {
  enabled: false,
  version: EXPANSION_MODEL_VERSION,
  components: [
    { key: 'seatHeadroom', label: 'Seat headroom (FTE : active users)', weight: 1, enabled: true, source: 'snapshotFact', bands: [] },
    { key: 'funding', label: 'Latest funding', weight: 1, enabled: true, source: 'snapshotFact', bands: [] },
    { key: 'sentiment', label: 'Sentiment (Red/Yellow/Green)', weight: 1, enabled: true, source: 'snapshotFact', bands: [] },
    { key: 'pacing', label: 'Contract pacing', weight: 1, enabled: true, source: 'snapshotFact', bands: [] },
    { key: 'execEngagement', label: 'Exec engagement', weight: 1, enabled: true, source: 'snapshotFact', bands: [] },
    { key: 'empGrowthYoy', label: 'Employee growth (YoY)', weight: 1, enabled: true, source: 'snapshotFact', bands: [] },
    { key: 'peBacked', label: 'PE-backed (penalty)', weight: -10, enabled: true, source: 'snapshotFact', bands: [] },
    { key: 'recentCw', label: 'Recent Closed Won (penalty)', weight: -10, enabled: true, source: 'snapshotFact', bands: [] },
    { key: 'amendmentBonus', label: 'Open amendment (bonus)', weight: 10, enabled: true, source: 'snapshotFact', bands: [] },
    { key: 'fundingEvent', label: 'Recent funding event (bonus)', weight: 10, enabled: true, source: 'snapshotFact', bands: [] },
  ],
};

/** A single line in the read-only Expansion Score rubric (display-only; no runtime effect). */
export interface RubricItem {
  label: string;
  detail: string;
  weight: string;
}

/**
 * Read-only description of the Expansion Score v4 model, shown under "3. Expansion Scoring".
 * The actual score is precomputed in SQL (queries.ts expansionSql); this is documentation only.
 */
export const EXPANSION_RUBRIC: {
  base: RubricItem[];
  modifier: RubricItem[];
  penalties: RubricItem[];
  bonuses: RubricItem[];
  note: string;
} = {
  base: [
    { label: 'Seat Headroom', detail: 'FTE per active-L90 Clay user (percentile). Excluded if HC/usage unknown.', weight: '20' },
    { label: 'HC Growth YoY', detail: 'Year-over-year headcount % change (percentile). Excluded with no year-ago baseline.', weight: '25' },
    { label: 'Funding / Scale', detail: 'Two-track: funding amount (<2,000 employees) or headcount (≥2,000).', weight: '10' },
    { label: 'Pacing', detail: 'Credit pacing vs target → 0/25/50/100 bucket. Excluded in the first 90 days (grace).', weight: '25' },
    { label: 'Exec Engagement', detail: 'Director+/exec touchpoints in the last 365 days (percentile).', weight: '10' },
  ],
  modifier: [
    { label: 'Health', detail: 'SFDC account health (Gong sentiment fallback).', weight: 'Green +20 / Yellow 0 / Red −20' },
  ],
  penalties: [
    { label: 'PE-backed', detail: 'Last funding type is Private Equity.', weight: '−10' },
    { label: 'Multi-year contract', detail: 'Contract term greater than 12 months.', weight: '−5' },
    { label: 'Contraction', detail: 'Current ARR below first enterprise ARR (first ARR ≥ $50k).', weight: '−5' },
    { label: 'Recently renewed (L3M)', detail: 'Closed Won in the last 3 months.', weight: '−5' },
    { label: 'Recently renewed (L3-6M)', detail: 'Closed Won 3-6 months ago and pacing under 100%.', weight: '−5' },
    { label: 'Saturation', detail: 'Current ARR at least 3× first enterprise ARR (first ARR ≥ $50k).', weight: '−5' },
  ],
  bonuses: [
    { label: 'Open amendment', detail: 'Open Amendment opp (positive amount, future close, live quote).', weight: '+10' },
    { label: 'Funding event', detail: 'IPO in the last 180 days or a new funding round in the last 90 days.', weight: '+10' },
    { label: 'Team penetration — Marketing', detail: '≥70% of first-30-day Clay actions by Marketing personas.', weight: '+5' },
    { label: 'Team penetration — Operations', detail: '≥70% of first-30-day Clay actions by Operations personas.', weight: '+3' },
  ],
  note: 'Startup override: companies under 2,000 employees with Series or IPO funding in the last 24 months have all expansion/contraction penalties halved. Final score = weighted base (the denominator shrinks when Seat, HC Growth, or Pacing is excluded) + health modifier − penalties + bonuses.',
};

export const DEFAULT_WEIGHTS: CarveWeights = {
  q2: 400,
  q3: 400,
  q4: 400,
  accounts: 2,
  arrSpreadK: 2,
  expansionSpread: 0,
  newOwnerArrBalanceK: 25,
  newOwnerQuarterSpread: 50,
  // Expansion is a balance dimension, ON by default at its base coefficient (importance 50). It spreads
  // expansion potential evenly across books (routing) rather than concentrating the best in new books.
  newOwnerExpansionK: 4,
  preChange: 0,
  churn: 0.5,
  recencyPref: 20,
  // Penalty per reassigned Red-health account (strong vs churn/recency, but still allows moves when required).
  redHealthPenaltyK: 40,
};

/**
 * The foundational carve logic (v2): new-books-only by default, a single 25% movement cap,
 * count-driven seeding with representative donor sampling, and hard region enforcement for STRAT.
 */
export const DEFAULT_BASE_CONFIG: CarveConfig = {
  scope: 'newhire',
  newOwners: [],
  movementCapPct: 0.25,
  perRepOverrides: {},
  pinnedAssignments: [],
  repRegions: {},
  repManagers: {},
  enforceRegion: false,
  regionSource: 'owner',
  // Re-owned lock targets 6 months; the engine auto-relaxes it down to the floor (45d) only as far as needed.
  locks: { renewingWithinDays: 90, onboardingWithinDays: 90, ownedRecentlyDays: 180, ownedRecentlyFloorDays: 90, autoRelaxOwnedRecently: true },
  excludedReps: [],
  frozenReps: [],
  // Per-account ARR figure used to derive each book's total ARR target (ENT 20 × $80k ≈ $1.6m).
  newOwnerTargetAvgArr: 80000,
  // Outlier-robust ARR balancing: winsorize account ARR at the 95th percentile for balance math only.
  arrBalanceWinsorPct: 95,
  // SoT "No Reassignments Logic #5": Red-health accounts are HARD-locked to their current GS
  // (auto-pinned in engine/optimizer.ts). Setting to false relaxes to the soft penalty layer
  // controlled by protectRedHealth / redHealthPenaltyK below.
  lockRedHealth: true,
  // Secondary soft penalty for Red-health moves — only active when lockRedHealth is false.
  protectRedHealth: true,
  // Dummy / expected-new-business seat seeding (forecast-driven) is on by default; each book's
  // seats come from its expectedAccounts (0 seats = all real donors, so this is a safe default).
  includeDummyAccounts: true,
  // Guarantee each new book this many REAL accounts before the general fill (renewal ballast).
  renewalBallast: 2,
  // SoT Criteria 7: front-load new-hire books with renewals from the 2 quarters BEFORE each
  // book's ramp date so ramping reps land on a balanced renewal cadence (a Q1 starter gets
  // Q3/Q4 renewals in the initial carve). Only takes effect when at least one newOwner is
  // configured. Operators can flip false per-run when they want an evenly-spread book.
  frontLoadNewBooks: true,
  weights: { ...DEFAULT_WEIGHTS },
  expansionModel: DEFAULT_EXPANSION_MODEL,
  seed: 7,
  maxIters: 60000,
  timeLimitMs: 1500,
};

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? (T[K] extends unknown[] ? T[K] : DeepPartial<T[K]>) : T[K];
};

export type CarveConfigPatch = DeepPartial<CarveConfig>;

const num = (v: unknown, fallback: number): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

/** Clean an expansion model: valid weights, sorted bands (null catch-all last), boolean flags. */
function normalizeExpansionModel(model?: Partial<ExpansionModel> | null): ExpansionModel {
  if (!model || !Array.isArray(model.components) || model.components.length === 0) {
    return { ...DEFAULT_EXPANSION_MODEL, components: DEFAULT_EXPANSION_MODEL.components.map((c) => ({ ...c, bands: [...c.bands] })) };
  }
  const components: ExpansionComponent[] = model.components
    .filter((c): c is ExpansionComponent => !!c && typeof c.key === 'string' && c.key.trim().length > 0)
    .map((c) => {
      const bands = Array.isArray(c.bands)
        ? c.bands
            .filter((b) => b && (b.upTo === null || Number.isFinite(Number(b.upTo))))
            .map((b) => ({ upTo: b.upTo === null ? null : Number(b.upTo), score: num(b.score, 0) }))
            .sort((a, b) => {
              if (a.upTo === null) return 1;
              if (b.upTo === null) return -1;
              return a.upTo - b.upTo;
            })
        : [];
      return {
        key: c.key.trim(),
        label: typeof c.label === 'string' && c.label.trim() ? c.label.trim() : c.key.trim(),
        weight: num(c.weight, 1),
        enabled: c.enabled !== false,
        source: c.source === 'query' ? 'query' : 'snapshotFact',
        ...(c.source === 'query' && typeof c.sql === 'string' ? { sql: c.sql } : {}),
        bands,
      };
    });
  return {
    enabled: model.enabled === true,
    components,
    ...(typeof model.version === 'number' ? { version: model.version } : {}),
  };
}

/**
 * One-time backfill for legacy carves: if a stored expansion model predates the current stock
 * schema version, merge in any missing built-in components (using their default definitions) while
 * preserving every existing component's edited weight/label/bands/enabled and any custom fields.
 * Returns the (possibly upgraded) model plus whether anything changed, so callers can persist once.
 */
export function upgradeExpansionModel(model?: Partial<ExpansionModel> | null): { model: ExpansionModel; changed: boolean } {
  const normalized = normalizeExpansionModel(model);
  if (normalized.version === EXPANSION_MODEL_VERSION) return { model: normalized, changed: false };

  const have = new Set(normalized.components.map((c) => c.key));
  const missing = DEFAULT_EXPANSION_MODEL.components.filter((c) => !have.has(c.key));

  // Re-order: stock components in canonical order (existing edits preserved), then custom fields.
  const byKey = new Map(normalized.components.map((c) => [c.key, c]));
  const stockOrdered: ExpansionComponent[] = STOCK_EXPANSION_KEYS
    .map((key) => byKey.get(key) ?? DEFAULT_EXPANSION_MODEL.components.find((c) => c.key === key))
    .filter((c): c is ExpansionComponent => !!c)
    .map((c) => ({ ...c, bands: [...c.bands] }));
  const customComponents = normalized.components.filter((c) => !STOCK_EXPANSION_KEYS.includes(c.key as (typeof STOCK_EXPANSION_KEYS)[number]));

  const upgraded: ExpansionModel = {
    enabled: normalized.enabled,
    version: EXPANSION_MODEL_VERSION,
    components: [...stockOrdered, ...customComponents],
  };
  // Changed if we added components or had to stamp/normalize the version.
  const changed = missing.length > 0 || normalized.version !== EXPANSION_MODEL_VERSION;
  return { model: upgraded, changed };
}

/** Merge a (possibly partial / LLM-produced) patch onto a base and normalize into a valid config. */
export function normalizeConfig(base: CarveConfig, patch?: CarveConfigPatch | null): CarveConfig {
  const p = patch ?? {};
  const locks = { ...base.locks, ...(p.locks ?? {}) };
  const weights = { ...base.weights, ...((p.weights as Partial<CarveWeights>) ?? {}) };

  const out: CarveConfig = {
    scope: (p.scope as CarveConfig['scope']) ?? base.scope ?? 'newhire',
    newOwners: (p.newOwners as CarveConfig['newOwners']) ?? base.newOwners,
    movementCapPct: num(p.movementCapPct, base.movementCapPct ?? DEFAULT_BASE_CONFIG.movementCapPct),
    perRepOverrides: (p.perRepOverrides as CarveConfig['perRepOverrides']) ?? base.perRepOverrides,
    pinnedAssignments: (p.pinnedAssignments as CarveConfig['pinnedAssignments']) ?? base.pinnedAssignments,
    repRegions: (p.repRegions as CarveConfig['repRegions']) ?? base.repRegions ?? {},
    repManagers: (p.repManagers as CarveConfig['repManagers']) ?? base.repManagers ?? {},
    enforceRegion: p.enforceRegion ?? base.enforceRegion ?? false,
    // Inert (regions always resolve from the current rep); preserved so stored run hashes stay valid.
    regionSource: (p.regionSource ?? base.regionSource) === 'contacts' ? 'contacts' : 'owner',
    locks: {
      renewingWithinDays: locks.renewingWithinDays === false ? false : num(locks.renewingWithinDays, 90),
      onboardingWithinDays: locks.onboardingWithinDays === false ? false : num(locks.onboardingWithinDays, 90),
      ownedRecentlyDays: locks.ownedRecentlyDays === false ? false : num(locks.ownedRecentlyDays, 180),
      ownedRecentlyFloorDays: Math.max(0, num(locks.ownedRecentlyFloorDays, 90)),
      autoRelaxOwnedRecently: locks.autoRelaxOwnedRecently !== false,
    },
    excludedReps: (p.excludedReps as string[]) ?? base.excludedReps,
    frozenReps: (p.frozenReps as string[]) ?? base.frozenReps,
    newOwnerTargetAvgArr: num(p.newOwnerTargetAvgArr, base.newOwnerTargetAvgArr ?? DEFAULT_BASE_CONFIG.newOwnerTargetAvgArr),
    frontLoadNewBooks: p.frontLoadNewBooks ?? base.frontLoadNewBooks ?? false,
    expansionModel: normalizeExpansionModel((p.expansionModel as Partial<ExpansionModel>) ?? base.expansionModel),
    weights: {
      q2: num(weights.q2, DEFAULT_WEIGHTS.q2),
      q3: num(weights.q3, DEFAULT_WEIGHTS.q3),
      q4: num(weights.q4, DEFAULT_WEIGHTS.q4),
      accounts: num(weights.accounts, DEFAULT_WEIGHTS.accounts),
      arrSpreadK: num(weights.arrSpreadK, DEFAULT_WEIGHTS.arrSpreadK),
      expansionSpread: num(weights.expansionSpread, DEFAULT_WEIGHTS.expansionSpread),
      newOwnerArrBalanceK: num(weights.newOwnerArrBalanceK, DEFAULT_WEIGHTS.newOwnerArrBalanceK),
      newOwnerQuarterSpread: num(weights.newOwnerQuarterSpread, DEFAULT_WEIGHTS.newOwnerQuarterSpread),
      newOwnerExpansionK: num(weights.newOwnerExpansionK, DEFAULT_WEIGHTS.newOwnerExpansionK),
      preChange: num(weights.preChange, DEFAULT_WEIGHTS.preChange),
      churn: num(weights.churn, DEFAULT_WEIGHTS.churn),
      recencyPref: num(weights.recencyPref, DEFAULT_WEIGHTS.recencyPref),
      redHealthPenaltyK: num(weights.redHealthPenaltyK, DEFAULT_WEIGHTS.redHealthPenaltyK),
    },
    arrBalanceWinsorPct: Math.min(100, Math.max(0, num(p.arrBalanceWinsorPct, base.arrBalanceWinsorPct ?? 95))),
    lockRedHealth: p.lockRedHealth ?? base.lockRedHealth ?? true,
    protectRedHealth: p.protectRedHealth ?? base.protectRedHealth ?? true,
    includeDummyAccounts: p.includeDummyAccounts ?? base.includeDummyAccounts ?? true,
    renewalBallast: Math.min(10, Math.max(0, Math.round(num(p.renewalBallast, base.renewalBallast ?? 2)))),
    excludedAccountIds: [...new Set(((p.excludedAccountIds as string[]) ?? base.excludedAccountIds ?? []).filter((id) => typeof id === 'string' && id.trim()))],
    seed: num(p.seed, base.seed),
    maxIters: Math.max(0, num(p.maxIters, base.maxIters)),
    timeLimitMs: Math.min(8000, Math.max(200, num(p.timeLimitMs, base.timeLimitMs))),
  };
  // Existing-GS targets are optional — only carry them through when set on the patch or base.
  if (p.existingTargetCount != null || base.existingTargetCount != null)
    out.existingTargetCount = Math.max(0, num(p.existingTargetCount, base.existingTargetCount ?? 0));
  if (p.existingTargetArr != null || base.existingTargetArr != null)
    out.existingTargetArr = Math.max(0, num(p.existingTargetArr, base.existingTargetArr ?? 0));
  // Full Book rate override — only carry through when explicitly set (else the UI uses the historical stat).
  if (p.estNewBizPerQuarter != null || base.estNewBizPerQuarter != null)
    out.estNewBizPerQuarter = Math.max(0, num(p.estNewBizPerQuarter, base.estNewBizPerQuarter ?? 0));
  // Cutover date (Rebalance/Combined) — carry through only when set and a valid ISO date.
  const cutover = p.cutoverDate !== undefined ? p.cutoverDate : base.cutoverDate;
  if (cutover && Number.isFinite(Date.parse(`${cutover}T00:00:00Z`))) out.cutoverDate = cutover;
  return out;
}

/** Stable, order-independent hash of a config (for the deterministic result cache). */
export function hashConfig(config: CarveConfig): string {
  const stable = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(stable);
    if (v && typeof v === 'object') {
      return Object.keys(v as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((acc, k) => {
          acc[k] = stable((v as Record<string, unknown>)[k]);
          return acc;
        }, {});
    }
    return v;
  };
  const s = JSON.stringify(stable(config));
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(16);
}

export interface ConfigDiffEntry {
  path: string;
  before: unknown;
  after: unknown;
}

/** Field-by-field diff of two configs (for review UI + vote proposals). */
export function diffConfig(before: CarveConfig, after: CarveConfig): ConfigDiffEntry[] {
  const entries: ConfigDiffEntry[] = [];
  const walk = (a: unknown, b: unknown, path: string) => {
    if (JSON.stringify(a) === JSON.stringify(b)) return;
    const isObj = (x: unknown) => x && typeof x === 'object' && !Array.isArray(x);
    if (isObj(a) && isObj(b)) {
      const keys = new Set([...Object.keys(a as object), ...Object.keys(b as object)]);
      for (const k of keys) walk((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k], path ? `${path}.${k}` : k);
    } else {
      entries.push({ path, before: a, after: b });
    }
  };
  walk(before, after, '');
  return entries;
}

const money = (n: number) => `$${Math.round(n).toLocaleString()}`;

/**
 * Fiscal-quarter label for a date (e.g. "2026-08-01" -> "26Q3"), mirroring the FY boundaries used in
 * queries.ts: Feb-Apr=Q1, May-Jul=Q2, Aug-Oct=Q3, Nov-Jan=Q4 (January rolls into the prior FY's Q4).
 * Display-only; used for the base-logic summary and the setup UI.
 */
export function fiscalQuarterLabel(dateStr?: string | null): string | null {
  if (!dateStr) return null;
  const ms = Date.parse(dateStr);
  if (!Number.isFinite(ms)) return null;
  const d = new Date(ms);
  const month = d.getUTCMonth() + 1;
  const year = d.getUTCFullYear();
  const q = month >= 2 && month <= 4 ? 1 : month >= 5 && month <= 7 ? 2 : month >= 8 && month <= 10 ? 3 : 4;
  const fyYear = month === 1 ? year - 1 : year;
  return `${String(fyYear).slice(-2)}Q${q}`;
}

/** Render a config as human-readable markdown for the "base logic" panel and proposals. */
export function summarizeConfig(config: CarveConfig): string {
  const lines: string[] = [];
  const scopeText: Record<CarveConfig['scope'], string> = {
    newhire: 'Seed new books only — existing reps donate but are never rebalanced against each other (no EGS-to-EGS swaps).',
    rebalance: 'Rebalance existing books only (enables the swap phase); no new books created.',
    combined: 'Carve new books and rebalance all existing books (enables the swap phase).',
  };
  lines.push(`**Scope:** ${scopeText[config.scope] ?? config.scope}`);
  if (config.scope !== 'newhire' && config.cutoverDate)
    lines.push(`**Cutover date:** ${config.cutoverDate} (assignment date sent to Clay for moves between current reps)`);

  if (config.newOwners.length) {
    const pinnedByGs = new Map<string, number>();
    for (const p of config.pinnedAssignments) pinnedByGs.set(p.gs, (pinnedByGs.get(p.gs) ?? 0) + 1);
    lines.push(
      `**New books to carve:** ${config.newOwners
        .map((n) => {
          const targetArr = n.targetArr ?? n.targetCount * (n.targetAvgArr ?? config.newOwnerTargetAvgArr);
          const region = config.enforceRegion && n.region ? `, ${n.region}` : '';
          const fq = fiscalQuarterLabel(n.startDate);
          const ramp = n.startDate ? ` · books ${rampDateFor(n.startDate)}` : '';
          const start = n.startDate ? ` · starts ${n.startDate}${fq ? ` (${fq})` : ''}${ramp}` : ramp;
          const pinned = pinnedByGs.get(n.name) ?? 0;
          if (pinned >= n.targetCount) return `${n.name} → ${n.targetCount} accounts (pinned, fixed book${region})${start}`;
          if (pinned > 0)
            return `${n.name} → ${n.targetCount} accts (${pinned} pinned + ${n.targetCount - pinned} seeded, ~${money(targetArr)} target${region})${start}`;
          return `${n.name} → ${n.targetCount} accts (~${money(targetArr)} target${region})${start}`;
        })
        .join('; ')}`,
    );
  }

  // Rule 1 — locks (never donated).
  const lockBits: string[] = [];
  const hasBooks = config.newOwners.length > 0;
  if (config.locks.onboardingWithinDays !== false)
    lockBits.push(
      `onboarding (< ${config.locks.onboardingWithinDays}d)` +
        (hasBooks ? " (measured at each new book's ramp date)" : ''),
    );
  if (config.locks.renewingWithinDays !== false) {
    lockBits.push(
      `renewing within ${config.locks.renewingWithinDays}d` +
        (hasBooks ? " (measured at each new book's ramp date)" : ''),
    );
  }
  if (config.locks.ownedRecentlyDays !== false) {
    const floor = config.locks.ownedRecentlyFloorDays ?? 90;
    const relax =
      config.locks.autoRelaxOwnedRecently !== false
        ? ` (auto-relaxes ${config.locks.ownedRecentlyDays} → ${floor}d, only as far as needed to fill the new books)`
        : '';
    lockBits.push(`re-owned within ${config.locks.ownedRecentlyDays}d${relax}`);
  }
  if (config.movementCapPct < 1) {
    lockBits.push(`cap donor loss at ${Math.round(config.movementCapPct * 100)}% of a donor's pre-book`);
  }
  if (config.lockRedHealth !== false) {
    lockBits.push('Red-health accounts hard-locked to current GS');
  } else if (config.protectRedHealth !== false) {
    lockBits.push('protect Red-health accounts (soft penalty)');
  }
  lines.push(
    `**1. Base Rules:** ${lockBits.length ? lockBits.join(', ') : 'none'}. Pinned / frozen accounts are also immovable.`,
  );
  const winsorPct = config.arrBalanceWinsorPct ?? 95;
  if (winsorPct < 100) {
    lines.push(`**ARR outlier cap:** balance math winsorizes account ARR at the ${winsorPct}th percentile (display ARR unchanged).`);
  }

  // Rule 3 — seeding + balance.
  lines.push(
    '**3. Seeding & balance:** account count is the primary target (ENT ~20, STRAT ~10, configurable per book). Sourcing is representative — prefer donors with the most remaining cap and rotate across each donor\u2019s ARR quartiles. New books are balanced toward their ARR target and renewal-quarter spread; expansion score is a light tiebreaker; longer-tenured accounts move first.',
  );

  if (config.scope !== 'newhire' && (config.existingTargetCount != null || config.existingTargetArr != null)) {
    const bits: string[] = [];
    if (config.existingTargetCount != null) bits.push(`${config.existingTargetCount} accts`);
    if (config.existingTargetArr != null) bits.push(money(config.existingTargetArr));
    lines.push(`**Existing-GS target (fully ramped):** ${bits.join(' · ')} (per-rep overrides win; balanced toward target while respecting the donor-loss cap).`);
  }

  if (config.frontLoadNewBooks) {
    lines.push('**Uneven distribution (front-load):** ~2/3 of each new book is sourced from accounts renewing in the 2 quarters before that book\u2019s ramp date (overrides the onboarding lock for those picks).');
  }

  if (config.enforceRegion) {
    lines.push('**Geo Lock (hard):** each new book / regioned rep takes only accounts whose recommended region matches.');
  }

  if (config.expansionModel?.enabled) {
    const parts = config.expansionModel.components
      .filter((c) => c.enabled)
      .map((c) => `${c.label} (x${c.weight}${c.bands.length ? `, ${c.bands.length} bands` : ''})`);
    lines.push(`**Expansion model (custom):** ${parts.length ? parts.join(', ') : 'no active components'}.`);
  }

  if (config.includeDummyAccounts !== false && (config.renewalBallast ?? 2) > 0 && config.newOwners.length) {
    lines.push(`**Renewal ballast:** each new book gets at least ${config.renewalBallast ?? 2} REAL accounts before the general fill (expected-new-biz seats leave that much room under the target).`);
  }
  if (config.excludedAccountIds?.length) {
    lines.push(`**Excluded accounts:** ${config.excludedAccountIds.length} removed from this carve entirely (exclusion beats pins).`);
  }
  if (config.frozenReps.length) lines.push(`**Frozen reps (no moves):** ${config.frozenReps.join(', ')}.`);
  if (config.excludedReps.length) lines.push(`**Excluded reps:** ${config.excludedReps.join(', ')}.`);
  const overrides = Object.entries(config.perRepOverrides);
  if (overrides.length) {
    lines.push(
      `**Per-rep overrides:** ${overrides
        .map(([rep, ov]) => {
          const bits: string[] = [];
          if (ov.frozen) bits.push('frozen');
          if (ov.noHandoffs) bits.push('no handoffs');
          if (ov.donateAll) bits.push('donate all');
          if (ov.maxAdded != null) bits.push(`max +${ov.maxAdded}`);
          if (ov.maxLost != null) bits.push(`max -${ov.maxLost}`);
          return `${rep} (${bits.join(', ')})`;
        })
        .join('; ')}.`,
    );
  }
  if (config.pinnedAssignments.length) {
    lines.push(`**Pinned accounts:** ${config.pinnedAssignments.length} held fixed to a specified GS.`);
  }
  return lines.join('\n\n');
}
